import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    CountryCode,
    useIntl,
    useLocalisationContext
} from '@olxpwa-lib/i18n/client';
import { CostCalculator } from '~components/AdServiceBooking/CostCalculator';

import type { BookingCalendarFormValue } from '~components/AdServiceBooking/types';
import { useBookingCalendarContainer } from '~hooks';

import {
    BookingDescription,
    BookingDivider,
    BookingInfoLink,
    BookingInfoLinkWrapper,
    BookingTitle
} from '~components/AdServiceBooking';
import { DatePicker } from '~components/shared';
import { Platform, LoggedUser } from '~types';
import { adTracking } from '~tracking';
import { TestId } from '~testId';
import { useAppContext } from '~state/App.context';
import { bookingHowToUseHelpLink } from '~constants';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    DATE_FROM_DEFINED,
    DATE_FROM_NOT_DEFINED,
    RENDER_MOBILE_SUBMIT_BUTTON
} from '~events/constants';
import { publish } from '~events';
import { BookingAdditionalInformation } from '~components/AdServiceBooking/BookingAdditionalInformation/BookingAdditionalInformation';
import { AdData } from '@olxpwa-lib/utils-ad/types';
import { useBookingCalendarHandleSubmit } from '~hooks/useBookingCalendarHandleSubmit';
import { SubmitButton } from '~components/AdServiceBooking/SubmitButton/SubmitButton';
import { validationSchema } from './validation/schemas';
import { calendarTranslations } from './translations';
import BlockDatesLegend from './BlockDatesLegend/BlockDatesLegend';
import { modifiers } from './utils';
import { getMinimumStayParamFromAd } from '~components/shared/DatePicker/utils';

export const FIELD_NAME_NOTE = 'note';

export type BookingCalendarProps = {
    ad: AdData;
    platform: Platform;
    user: LoggedUser;
    defaultMonth?: Date | undefined;
    renderBookingSubmitMobile?: (element: JSX.Element) => void;
};

const BookingCalendar = ({
    ad,
    user,
    defaultMonth,
    platform,
    renderBookingSubmitMobile
}: BookingCalendarProps) => {
    const { formatMessage } = useIntl();
    const bookingFormRef = useRef<HTMLFormElement | null>(null);

    const methods = useForm<BookingCalendarFormValue>({
        defaultValues: {
            dateFrom: undefined,
            dateTo: undefined,
            note: undefined
        },
        resolver: yupResolver(validationSchema(formatMessage)),
        mode: 'all'
    });
    const {
        reset,
        formState: { errors },
        watch
    } = methods;
    const [dateFrom, dateTo, note] = watch(['dateFrom', 'dateTo', 'note']);
    const {
        tracker,
        PWAutils,
        SFCUtils: { useServicesOfferConfig }
    } = useAppContext();
    const { dispatchToastMessage } = PWAutils;

    const { trackEvent } = tracker;
    const {
        bookingCalendar: {
            isShortTermRentals: isStr,
            renderAvailabilityCalendar,
            isGuestsFeatureEnabled
        }
    } = useServicesOfferConfig(ad.id, true);
    const isUserAdOwner = user.userId === ad.user.id;

    const enableBookingSummaryFeature = renderAvailabilityCalendar && isStr;
    const displayBlockDatesFeature =
        isUserAdOwner && renderAvailabilityCalendar;
    const { bookingDescription, bookingTitle } = calendarTranslations(
        displayBlockDatesFeature,
        renderAvailabilityCalendar
    );
    const {
        marketMetadata: { countryCode }
    } = useLocalisationContext();
    const [formValues, setFormValues] = useState<BookingCalendarFormValue>({});

    const { availabilityData, isSelectedDatesInBlockDates, onBlockDates } =
        useBookingCalendarContainer({
            showAvailability: renderAvailabilityCalendar,
            ad,
            isStr,
            displayBlockDatesFeature,
            isGuestsFeatureEnabled,
            reset
        });

    const { isLoading, onSubmit, onSubmitWithAvailability } =
        useBookingCalendarHandleSubmit({
            showAvailability: renderAvailabilityCalendar,
            ad,
            isStr,
            displayBlockDatesFeature,
            isGuestsFeatureEnabled,
            reset,
            onBlockDates
        });

    const isDateInBlockedDates = isSelectedDatesInBlockDates(
        formValues.dateFrom,
        formValues.dateTo
    );

    const { datePickerModifiers } = modifiers(
        renderAvailabilityCalendar,
        availabilityData,
        displayBlockDatesFeature
    );

    const checkIsRequestButtonDisabled = useCallback(() => {
        if (
            isStr &&
            (!dateFrom || !dateTo || dateFrom.toString() === dateTo.toString())
        ) {
            return true;
        }
        return !dateFrom;
    }, [isStr, dateFrom, dateTo]);

    const isRequestButtonDisabled = checkIsRequestButtonDisabled();

    const handleFormValuesChanges = useCallback(
        (values: BookingCalendarFormValue) => {
            setFormValues(values);
            if (values.dateFrom) {
                publish(DATE_FROM_DEFINED);
            } else {
                publish(DATE_FROM_NOT_DEFINED);
            }
        },
        []
    );

    const handleSubmit = (values: BookingCalendarFormValue) => {
        if (
            platform === Platform.MOBILE &&
            (!values.dateFrom || !values.dateTo) &&
            !displayBlockDatesFeature
        ) {
            bookingFormRef?.current &&
                bookingFormRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest'
                });

            dispatchToastMessage?.(
                formatMessage({
                    id: 'service.booking.ad.request.button.error.select.valid.dates',
                    defaultMessage: 'Select a date'
                }),
                10000,
                'left'
            );

            return;
        }
        if (renderAvailabilityCalendar) {
            onSubmitWithAvailability(values);
        } else {
            onSubmit(values);
        }
    };

    useEffect(() => {
        if (errors[FIELD_NAME_NOTE]) {
            trackEvent(adTracking.events.bookingNoteError, {
                touch_point_button: renderAvailabilityCalendar
                    ? adTracking.touchPointButton.bookingAvailabilityCalendar
                    : adTracking.touchPointButton.bookingBasicCalendar
            });
        }
    }, [errors, renderAvailabilityCalendar, trackEvent, note]);

    useEffect(() => {
        publish(RENDER_MOBILE_SUBMIT_BUTTON);
    }, []);

    return (
        <FormProvider {...methods}>
            <form
                ref={bookingFormRef}
                onSubmit={methods.handleSubmit(handleSubmit)}
                data-testid={TestId.bookingCalendarComponent}
            >
                <BookingTitle>
                    {formatMessage({
                        ...bookingTitle
                    })}
                </BookingTitle>
                <BookingDescription>
                    {formatMessage({
                        ...bookingDescription
                    })}
                </BookingDescription>
                <DatePicker
                    isStr={isStr}
                    defaultMonth={defaultMonth}
                    showAvailability={renderAvailabilityCalendar}
                    bookedDates={
                        renderAvailabilityCalendar
                            ? availabilityData.bookedDays
                            : []
                    }
                    modifiers={datePickerModifiers}
                    clearButtonDisabled={isRequestButtonDisabled}
                    setDatePickerValues={handleFormValuesChanges}
                    displayBlockDatesFeature={displayBlockDatesFeature}
                    isSelectedDateInBlockedDates={isDateInBlockedDates}
                    minimumStayRequirement={parseInt(
                        getMinimumStayParamFromAd(ad)?.value.toString() || '1'
                    )}
                />
                {!displayBlockDatesFeature && !enableBookingSummaryFeature && (
                    <>
                        {!dateFrom ? null : (
                            <>
                                <CostCalculator
                                    isStr={isStr}
                                    price={ad.price?.regularPrice?.value}
                                    currencyCode={
                                        ad.price?.regularPrice?.currencyCode
                                    }
                                />
                                <BookingAdditionalInformation />
                            </>
                        )}
                    </>
                )}
                {displayBlockDatesFeature && <BlockDatesLegend />}

                <SubmitButton
                    renderBookingSubmitMobile={renderBookingSubmitMobile}
                    ad={ad}
                    enableBookingSummaryFeature={enableBookingSummaryFeature}
                    isDateInBlockedDates={isDateInBlockedDates}
                    isLoading={isLoading}
                    displayBlockDatesFeature={displayBlockDatesFeature}
                    isRequestButtonDisabled={isRequestButtonDisabled}
                    isShortTermRentals={isStr}
                    renderAvailabilityCalendar={renderAvailabilityCalendar}
                />

                {renderAvailabilityCalendar && (
                    <>
                        <BookingDivider variant="secondary-light" />
                        <BookingInfoLinkWrapper>
                            <BookingInfoLink
                                variant="tertiary"
                                href={
                                    bookingHowToUseHelpLink[
                                        countryCode as CountryCode
                                    ]
                                }
                                target="_blank"
                                data-testid={TestId.bookingCalendarReadMore}
                                isMobile={platform === Platform.MOBILE}
                            >
                                {formatMessage({
                                    id: 'service.booking.ad.more.link',
                                    defaultMessage:
                                        'Read more about booking request at OLX'
                                })}
                            </BookingInfoLink>
                        </BookingInfoLinkWrapper>
                    </>
                )}
            </form>
        </FormProvider>
    );
};

export default BookingCalendar;
